import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Alert, Spinner, Badge, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faEye, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { getPendingApprovals, approveOrder, rejectOrder, getOrderDetails } from '../services/api';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { useAuth } from '../contexts/AuthContext';

function PendingApprovals() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState({ items: [] });
  const [actionLoading, setActionLoading] = useState(false);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [orderToAction, setOrderToAction] = useState(null);
  const { hasPermission } = useAuth();

  // Check if user has approve permission
  const hasApprovePermission = hasPermission('inventory', 'approve');

  const fetchPendingOrders = async () => {
    if (!hasApprovePermission) return;
    
    try {
      setLoading(true);
      const data = await getPendingApprovals();
      setOrders(data);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load pending approvals');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingOrders();
  }, [hasApprovePermission]);

  const handleViewDetails = async (order) => {
    try {
      setSelectedOrder(order);
      const details = await getOrderDetails(order.idTrans);
      setOrderDetails(details);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load order details:', error);
      toast.error('Failed to load order details');
    }
  };

  const handleApproveClick = (orderId) => {
    setOrderToAction(orderId);
    setShowApproveConfirm(true);
  };

  // A kit order's pick orders (one per warehouse) are approved and rejected
  // together by the server; the confirmations say so before it happens.
  const actionOrder = orders.find((o) => o.idTrans === orderToAction);
  const kitTogetherNote = (verb) => (Number(actionOrder?.kitPartCount) > 1 ? (
    <p className="small mb-0 mt-2" style={{ color: '#1e40af' }}>
      This is part of kit order <strong>{actionOrder.kitName} ×{actionOrder.kitQty}</strong>: all{' '}
      {actionOrder.kitPartCount} of its pick orders will be {verb} together.
    </p>
  ) : null);

  const handleApprove = async () => {
    try {
      setActionLoading(true);
      const result = await approveOrder(orderToAction);
      toast.success(result?.message || 'Order approved successfully');
      setShowApproveConfirm(false);
      fetchPendingOrders();
      
      // Trigger notification banner refresh
      window.dispatchEvent(new Event('refreshPendingCount'));
      
      if (showDetailModal && selectedOrder?.idTrans === orderToAction) {
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('Error approving order:', error);
      toast.error(error.response?.data?.message || 'Failed to approve order');
    } finally {
      setActionLoading(false);
      setOrderToAction(null);
    }
  };

  const handleRejectClick = (orderId) => {
    setOrderToAction(orderId);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectionReason || !rejectionReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }

    try {
      setActionLoading(true);
      const result = await rejectOrder(orderToAction, rejectionReason);
      toast.success(result?.message || 'Order rejected successfully');
      setShowRejectModal(false);
      setRejectionReason('');
      fetchPendingOrders();
      
      // Trigger notification banner refresh
      window.dispatchEvent(new Event('refreshPendingCount'));
      
      if (showDetailModal && selectedOrder?.idTrans === orderToAction) {
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('Error rejecting order:', error);
      toast.error(error.response?.data?.message || 'Failed to reject order');
    } finally {
      setActionLoading(false);
      setOrderToAction(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('MM/DD/YY HH:mm');
  };

  if (!hasApprovePermission) {
    return (
      <Container className="mt-4">
        <Alert variant="warning">
          You do not have permission to approve orders.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading pending approvals...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>
          Pending Order Approvals
          <Badge bg="warning" className="ms-2">{orders.length}</Badge>
        </h4>
        <Button variant="outline-primary" size="sm" onClick={fetchPendingOrders}>
          <FontAwesomeIcon icon={faRefresh} className="me-1" />
          Refresh
        </Button>
      </div>

      {orders.length === 0 ? (
        <Alert variant="success">
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          No pending orders to approve. All caught up!
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Order #</th>
              <th>Submitted By</th>
              <th>Submitted Date</th>
              <th>Customer Ref</th>
              <th>Items</th>
              <th>Ship To</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.idTrans}>
                <td>{order.idTrans}</td>
                <td>
                  {order.submittedByUsername || order.submittedByCustomerName || 'Unknown'}
                  {(order.submittedByFirstName || order.submittedByLastName) && (
                    <div className="text-muted small">
                      {order.submittedByFirstName} {order.submittedByLastName}
                    </div>
                  )}
                </td>
                <td>{formatDate(order.submittedDate)}</td>
                <td>{order.CustomerRef || '-'}</td>
                <td>
                  <Badge bg="info">{order.itemCount || 0} items</Badge>
                  {order.kitName && (
                    <div className="mt-1">
                      <Badge bg="primary">
                        Kit {order.kitName} ×{order.kitQty}
                        {Number(order.kitPartCount) > 1 ? ` · part ${order.kitPart} of ${order.kitPartCount}` : ''}
                      </Badge>
                    </div>
                  )}
                </td>
                <td>
                  {order.delName && (
                    <>
                      {order.delName}
                      {order.delCity && order.delState && (
                        <div className="text-muted small">
                          {order.delCity}, {order.delState}
                        </div>
                      )}
                    </>
                  )}
                  {!order.delName && (order.delNotes?.includes('HOLD') ? 
                    <Badge bg="warning">Hold for Pickup</Badge> : '-')}
                </td>
                <td>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleViewDetails(order)}
                      title="View Details"
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleApproveClick(order.idTrans)}
                      disabled={actionLoading}
                      title="Approve Order"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRejectClick(order.idTrans)}
                      disabled={actionLoading}
                      title="Reject Order"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Order Detail Modal */}
      <Modal size="lg" show={showDetailModal} onHide={() => setShowDetailModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Order Details - #{selectedOrder?.idTrans}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <>
              <div className="mb-3">
                <h6>Order Information</h6>
                <p className="mb-1"><strong>Submitted By:</strong> {selectedOrder.submittedByUsername || selectedOrder.submittedByCustomerName}</p>
                <p className="mb-1"><strong>Submitted Date:</strong> {formatDate(selectedOrder.submittedDate)}</p>
                <p className="mb-1"><strong>Customer Reference:</strong> {selectedOrder.CustomerRef || '-'}</p>
                <p className="mb-1"><strong>Status:</strong> <Badge bg="warning">Pending Approval</Badge></p>
              </div>

              {selectedOrder.delName && (
                <div className="mb-3">
                  <h6>Shipping Information</h6>
                  <p className="mb-1">{selectedOrder.delName}</p>
                  {selectedOrder.delAddress && <p className="mb-1">{selectedOrder.delAddress}</p>}
                  {(selectedOrder.delCity || selectedOrder.delState || selectedOrder.delZip) && (
                    <p className="mb-1">
                      {[selectedOrder.delCity, selectedOrder.delState, selectedOrder.delZip]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  )}
                  {selectedOrder.delPhone && <p className="mb-1">Phone: {selectedOrder.delPhone}</p>}
                </div>
              )}

              {selectedOrder.delNotes && (
                <div className="mb-3">
                  <h6>Notes</h6>
                  <p className="mb-0">{selectedOrder.delNotes}</p>
                </div>
              )}

              {orderDetails.items && orderDetails.items.length > 0 && (
                <div className="mb-3">
                  <h6>Order Items</h6>
                  <Table size="sm" striped bordered>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Description</th>
                        <th>Lot</th>
                        <th>Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderDetails.items.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.itemNumber || '-'}</td>
                          <td>{item.itemName || item.description || '-'}</td>
                          <td>{item.recTrans || '-'}</td>
                          <td>{item.transQty || item.quantity || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          <Button
            variant="success"
            onClick={() => handleApproveClick(selectedOrder.idTrans)}
            disabled={actionLoading}
          >
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            Approve Order
          </Button>
          <Button
            variant="danger"
            onClick={() => handleRejectClick(selectedOrder.idTrans)}
            disabled={actionLoading}
          >
            <FontAwesomeIcon icon={faTimes} className="me-1" />
            Reject Order
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve Confirmation Modal */}
      <Modal show={showApproveConfirm} onHide={() => !actionLoading && setShowApproveConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Approval</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Are you sure you want to approve order <strong>#{orderToAction}</strong>?</p>
          {kitTogetherNote('approved')}
          <p className="text-muted small mb-0 mt-2">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowApproveConfirm(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="success" 
            onClick={handleApprove}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Approving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                Yes, Approve Order
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => !actionLoading && setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reject Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please provide a reason for rejecting order <strong>#{orderToAction}</strong>:</p>
          {kitTogetherNote('rejected')}
          <Form.Group>
            <Form.Label>Rejection Reason <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter the reason for rejection..."
              disabled={actionLoading}
              isInvalid={rejectionReason.trim() === '' && rejectionReason.length > 0}
            />
            <Form.Control.Feedback type="invalid">
              Rejection reason is required.
            </Form.Control.Feedback>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleReject}
            disabled={actionLoading || !rejectionReason.trim()}
          >
            {actionLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Rejecting...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faTimes} className="me-1" />
                Reject Order
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default PendingApprovals;

